import React, { memo, useMemo, useCallback, type MouseEvent, type KeyboardEvent } from 'react';
import { styled } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { AvatarLite } from '@atlassian/jira-business-avatar-lite';
import { GROUP_BY_STATUS } from '@atlassian/jira-business-constants';
import StatusLozenge from '@atlassian/jira-common-components-status-lozenge';
import type { StatusCategory } from '@atlassian/jira-common-constants/src/status-categories';
import {
	CardKey,
	CardDueDate,
	CardType,
	CardLabels,
	CardTick,
	CardPriority,
	CardSpinner,
} from '@atlassian/jira-platform-card';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type { BoardIssueOld } from '../../../../../common/types';
import { useCardSubtasks } from '../../../../../controllers/card-subtasks-old';
import { useCardFieldsOld } from '../../../../../controllers/fields-preference';
import { useGroupBy } from '../../../../../controllers/group-by';
import CardDetailFields from './card-detail-fields';
import { CardSubtasks } from './card-subtasks';

type Props = {
	highlight?: string[];
	isHovered?: boolean;
	issue: BoardIssueOld;
	onIssueKeyClick: (event: MouseEvent<HTMLElement>) => void;
};

export const CardContent = memo(({ highlight, isHovered, issue, onIssueKeyClick }: Props) => {
	const [groupBy] = useGroupBy();
	const selectedFields = useCardFieldsOld(issue.issueTypeId);
	const { toggleSubtasks } = useCardSubtasks();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const isNewOptimisticIssue = useMemo(() => issue.key === '' || issue.key == null, [issue.key]);

	const labels = useMemo(() => {
		if (issue.issueDetails?.fieldsByAlias?.label != null) {
			return issue.issueDetails?.fieldsByAlias?.label.value;
		}
		return undefined;
	}, [issue.issueDetails]);

	const renderedLabels = useMemo(
		() =>
			labels && selectedFields.labels ? (
				<SingleFieldRow>
					<CardLabels labels={labels} highlight={highlight} />
				</SingleFieldRow>
			) : null,
		[selectedFields.labels, labels, highlight],
	);

	const renderedDueDate = useMemo(
		() =>
			issue.dueDate != null && selectedFields.duedate ? (
				<SingleFieldRow>
					<CardDueDate dueDate={issue.dueDate} isCompleted={issue.isDone} />
				</SingleFieldRow>
			) : null,
		[issue.dueDate, issue.isDone, selectedFields.duedate],
	);

	const renderedIssueType = useMemo(
		() =>
			issue.typeUrl != null && issue.typeName != null && selectedFields.issuetype ? (
				<IssueTypeIconContainer>
					<CardType uri={issue.typeUrl} text={issue.typeName} />
				</IssueTypeIconContainer>
			) : null,
		[issue.typeUrl, issue.typeName, selectedFields.issuetype],
	);

	const handleSubtaskClick = useCallback(
		(event: MouseEvent) => {
			// stop bento from opening
			event.preventDefault();
			event.stopPropagation();

			toggleSubtasks(issue.id);

			fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', 'jwmBoardCardSubtaskTrigger', {
				isExpanded: issue.subtasks != null,
			});
		},
		[toggleSubtasks, createAnalyticsEvent, issue.id, issue.subtasks],
	);

	const handleKeyDown = useCallback(
		(event: KeyboardEvent<HTMLDivElement>) => {
			if (event.key === 'Enter' || event.key === ' ') {
				toggleSubtasks(issue.id);
			}
		},
		[issue.id, toggleSubtasks],
	);

	const renderedSubtaskMetadata = useMemo(() => {
		if (issue.subtaskMetadata?.total && selectedFields.subtasks) {
			return (
				<SingleFieldRow
					// eslint-disable-next-line jira/integration/test-id-by-folder-structure
					data-testid="work-management-board.ui.board.column.card.card-content.subtask-container"
					onClick={handleSubtaskClick}
					tabIndex={0}
					onKeyPress={handleKeyDown}
					role="button"
				>
					<CardSubtasks
						subtaskMetadata={issue.subtaskMetadata}
						withSubtasks={issue.subtasks != null}
					/>
				</SingleFieldRow>
			);
		}
		return null;
	}, [
		issue.subtaskMetadata,
		issue.subtasks,
		selectedFields.subtasks,
		handleSubtaskClick,
		handleKeyDown,
	]);

	const renderedPriority = useMemo(
		() =>
			issue.priority && selectedFields.priority ? (
				<CardPriority uri={issue.priority.iconUrl} name={issue.priority.name} />
			) : null,
		[selectedFields.priority, issue.priority],
	);

	const renderedAssignee = useMemo(() => {
		if (issue.assignee?.id && selectedFields.assignee) {
			return (
				<AvatarLite
					highlight={highlight}
					avatarUrl={issue.assignee?.avatarUrl ?? ''}
					name={issue.assignee?.displayName ?? ''}
				/>
			);
		}

		return null;
	}, [selectedFields.assignee, issue.assignee, highlight]);

	const renderedKey = useMemo(
		() =>
			selectedFields.key && (
				<CardKey highlight={highlight} onClick={onIssueKeyClick} text={issue.key} />
			),
		[selectedFields.key, issue.key, highlight, onIssueKeyClick],
	);

	const renderedStatus = useMemo(
		() =>
			groupBy !== GROUP_BY_STATUS &&
			issue.statusCategory &&
			issue.statusName && (
				<SingleFieldRow>
					<StatusLozenge
						name={issue.statusName}
						// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
						category={issue.statusCategory as StatusCategory}
					/>
				</SingleFieldRow>
			),
		[groupBy, issue.statusCategory, issue.statusName],
	);

	return (
		<>
			{renderedStatus}
			{renderedLabels}
			{renderedDueDate}
			<MultiFieldRow>
				<LeftContentContainer>
					{renderedIssueType}
					{renderedKey}
				</LeftContentContainer>
				<FlowContentContainer>
					{issue.isDone && <CardTick />}
					{renderedPriority}
					{renderedAssignee}
				</FlowContentContainer>
			</MultiFieldRow>
			{renderedSubtaskMetadata}
			<CardDetailFields issue={issue} isHovered={isHovered} />
			{isNewOptimisticIssue && (
				<SpinnerContainer>
					<CardSpinner />
				</SpinnerContainer>
			)}
		</>
	);
});

const maxCardWidth = 270;
const menuActionButtonSize = 32;
const flowContentContainerStyles = {
	display: 'flex',
	alignItems: 'center',
	padding: `${token('space.050', '4px')} 0`,
	'& > div': { padding: `0 ${token('space.050', '4px')}` },
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SpinnerContainer = styled.div({
	position: 'absolute',
	top: token('space.200', '16px'),
	right: token('space.200', '16px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MultiFieldRow = styled.div({
	display: 'flex',
	width: `${maxCardWidth}px`,
	overflow: 'hidden',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LeftContentContainer = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	...flowContentContainerStyles,
	flex: 1,
	overflow: 'hidden',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SingleFieldRow = styled.div({
	display: 'flex',
	minWidth: `${maxCardWidth - menuActionButtonSize * 2}px`,
	paddingTop: token('space.050', '4px'),
	paddingBottom: token('space.050', '4px'),
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
const FlowContentContainer = styled.div(flowContentContainerStyles);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IssueTypeIconContainer = styled.span({
	marginRight: token('space.050', '4px'),
});
